import React, { useState } from 'react';
import { HelpCircle, MessageSquare, FileText, ChevronDown, Send, ExternalLink } from 'lucide-react';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { useLanguage } from '../../i18n/LanguageContext';
import { ZodiacTheme } from './zodiacThemes';
interface SupportTabProps {
  theme: ZodiacTheme;
  setActiveTab: (tab: string) => void;
}
const faqs = [{
  q: 'Как проходит консультация с астрологом?',
  a: 'Консультация проходит в выбранном вами формате: видео-звонок, аудио-звонок или текстовый чат. В назначенное время вы подключаетесь по ссылке в разделе «Мои консультации».'
}, {
  q: 'Как вернуть деньги за заказ?',
  a: 'Если астролог ещё не начал работу — отмените заказ в разделе «Мои заказы» с полным возвратом. Если работа начата — возврат рассматривается индивидуально через поддержку.'
}, {
  q: 'Как добавить профиль родственника?',
  a: 'Перейдите в «Мой астропрофиль» → «Добавить профиль». Сохраните данные близких для быстрого оформления заказов.'
}, {
  q: 'Как работает баланс?',
  a: 'Баланс — ваш кошелёк в рублях. Пополняйте картой, Apple Pay или Google Pay. При оплате средства списываются автоматически. Если не хватает — можно доплатить.'
}];
const docs = [{
  label: 'Правила платформы',
  href: '#'
}, {
  label: 'Политика возвратов',
  href: '#'
}, {
  label: 'Конфиденциальность',
  href: '#'
}];
export function SupportTab({
  theme,
  setActiveTab
}: SupportTabProps) {
  const {
    showToast
  } = useToast();
  const {
    t
  } = useLanguage();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [message, setMessage] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    showToast('Сообщение отправлено в поддержку');
    setMessage('');
  };
  return <div className="space-y-5 max-w-2xl pb-8" data-id="element-20712">
      {/* Header */}
      <div data-id="element-20713">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20714">Помощь</h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-20715">
          Ответы на вопросы и связь с поддержкой
        </p>
      </div>

      {/* FAQ */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-20716">
        <div className="px-4 py-3 border-b border-slate-100" data-id="element-20717">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-20718">
            <HelpCircle className="w-4 h-4 text-slate-400" data-id="element-20719" /> Частые вопросы
          </h3>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-20720">
          {faqs.map((faq, idx) => <div key={idx} data-id="element-20721">
              <button className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50/50 transition-colors" onClick={() => setOpenFaq(openFaq === idx ? null : idx)} data-id="element-20722">
                <span className="text-sm font-medium text-slate-900" data-id="element-20723">
                  {faq.q}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-20724" />
              </button>
              {openFaq === idx && <div className="px-4 pb-3 -mt-1" data-id="element-20725">
                  <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 rounded-lg p-3" data-id="element-20726">
                    {faq.a}
                  </p>
                </div>}
            </div>)}
        </div>
      </div>

      {/* Contact Form */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-20727">
        <div className="px-4 py-3 border-b border-slate-100" data-id="element-20728">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-20729">
            <MessageSquare className="w-4 h-4 text-slate-400" data-id="element-20730" /> Написать в
            поддержку
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-4" data-id="element-20731">
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Опишите вашу проблему..." className="w-full h-32 p-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none mb-3" data-id="element-20732" />
          <div className="flex justify-end" data-id="element-20733">
            <Button type="submit" variant="primary" size="sm" data-id="element-20734">
              <Send className="w-4 h-4 mr-2" data-id="element-20735" /> Отправить
            </Button>
          </div>
        </form>
      </div>

      {/* Documents */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-20736">
        <div className="px-4 py-3 border-b border-slate-100" data-id="element-20737">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2 mb-3" data-id="element-20738">
            <FileText className="w-4 h-4 text-slate-400" data-id="element-20739" /> Документы
          </h3>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-20740">
          {docs.map(doc => <a key={doc.label} href={doc.href} className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-xs font-medium text-slate-700 transition-colors" data-id="element-20741">
              <ExternalLink className="w-3 h-3 text-slate-400" data-id="element-20742" /> {doc.label}
            </a>)}
        </div>
      </div>
    </div>;
}